import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Copy,
  Pause,
  Play,
  RotateCcw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useCaseStream } from "hooks/useCaseDashboard";
import { parseLogLine } from "utils/caseDashboard";
import { highlight } from "../common/highlight";
import { Card, SecondaryButton, Notice } from "./parts";

const LEVEL_COLOR = {
  debug: "text-[#6B7280]",
  info: "text-[#E5E7EB]",
  warn: "text-[#FBBF24]",
  error: "text-[#F87171]",
};
const LEVEL_LABEL = { debug: "DBG", info: "", warn: "WARN", error: "ERROR" };

const LogsView = ({ caseId }) => {
  const { t } = useTranslation();
  const stream = useCaseStream(caseId, { maxLines: 3000 });
  const [follow, setFollow] = useState(true);
  const [problemsOnly, setProblemsOnly] = useState(false);
  const [source, setSource] = useState("");
  const [search, setSearch] = useState("");
  // Selected lines by their stream number (dropped + index), so a selection
  // survives old lines being trimmed off the front.
  const [selected, setSelected] = useState(() => new Set());
  const [anchor, setAnchor] = useState(null);
  const [copied, setCopied] = useState(null); // lines just copied
  const boxRef = useRef(null);
  const copiedTimer = useRef(null);
  const startRef = useRef(stream.start);
  startRef.current = stream.start;

  useEffect(() => {
    startRef.current("logs --tail 300");
  }, [caseId]);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  const offset = stream.dropped || 0;
  const parsed = useMemo(
    () =>
      stream.lines.map((raw, i) => ({ ...parseLogLine(raw), n: offset + i })),
    [stream.lines, offset]
  );
  const sources = useMemo(
    () => [...new Set(parsed.map((l) => l.tag).filter(Boolean))].sort(),
    [parsed]
  );
  const q = search.trim().toLowerCase();
  const shown = useMemo(
    () =>
      parsed.filter(
        (l) =>
          (!problemsOnly || l.level === "warn" || l.level === "error") &&
          (!source || l.tag === source) &&
          (!q || l.raw.toLowerCase().includes(q))
      ),
    [parsed, problemsOnly, source, q]
  );
  const filtered = !!q || problemsOnly || !!source;
  // Selected lines still in the buffer, in log order.
  const selectedLines = useMemo(
    () => parsed.filter((l) => selected.has(l.n)),
    [parsed, selected]
  );

  // Keeps following new lines while a filter is on.
  useEffect(() => {
    if (follow && boxRef.current)
      boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [shown, follow]);

  const clearSelection = useCallback(() => {
    setSelected(new Set());
    setAnchor(null);
  }, []);

  // A new stream numbers its lines from 0 again.
  useEffect(() => {
    if (stream.lines.length === 0 && offset === 0) clearSelection();
  }, [stream.lines.length, offset, clearSelection]);

  useEffect(() => {
    if (!selected.size) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") clearSelection();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected.size, clearSelection]);

  // Click toggles a line; shift-click adds the shown lines between the last
  // clicked one and this one.
  const onLineClick = (e, line) => {
    // Dragging to select text isn't a line click.
    if (!e.shiftKey && window.getSelection?.()?.toString()) return;
    if (e.shiftKey && anchor != null) {
      const from = shown.findIndex((l) => l.n === anchor);
      const to = shown.findIndex((l) => l.n === line.n);
      if (from !== -1 && to !== -1) {
        const [a, b] = from < to ? [from, to] : [to, from];
        setSelected((prev) => {
          const next = new Set(prev);
          for (const l of shown.slice(a, b + 1)) next.add(l.n);
          return next;
        });
        return;
      }
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(line.n)) next.delete(line.n);
      else next.add(line.n);
      return next;
    });
    setAnchor(line.n);
  };

  const copy = async (lines) => {
    if (!lines.length) return;
    try {
      await navigator.clipboard.writeText(lines.map((l) => l.raw).join("\n"));
      setCopied(lines.length);
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => setCopied(null), 1500);
    } catch {
      // clipboard unavailable
    }
  };

  const controlClass =
    "rounded-[10px] border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#374151] outline-none";

  return (
    <div className="flex h-full min-h-[480px] flex-col space-y-4">
      <h1 className="text-xl font-bold text-[#111827]">
        {t("caseDashboard.tabs.logs")}
      </h1>
      <div className="flex flex-wrap items-center gap-2">
        <select
          className={controlClass}
          value={problemsOnly ? "problems" : "all"}
          onChange={(e) => setProblemsOnly(e.target.value === "problems")}
          aria-label={t("caseDashboard.logs.level")}
        >
          <option value="all">{t("caseDashboard.logs.allLevels")}</option>
          <option value="problems">
            {t("caseDashboard.logs.problemsOnly")}
          </option>
        </select>
        <select
          className={controlClass}
          value={source}
          onChange={(e) => setSource(e.target.value)}
          aria-label={t("caseDashboard.logs.source")}
        >
          <option value="">{t("caseDashboard.logs.allSources")}</option>
          {sources.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <div className="flex min-w-[180px] flex-1 items-center gap-1.5 rounded-[10px] border border-[#E5E7EB] bg-white px-3 py-2 transition-colors focus-within:border-[#E07A3A]/70">
          <Search size={14} className="shrink-0 text-[#9CA3AF]" />
          <input
            className="min-w-0 flex-1 text-sm text-[#374151] outline-none"
            placeholder={t("caseDashboard.logs.search")}
            aria-label={t("caseDashboard.logs.search")}
            spellCheck={false}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {filtered && (
            <span className="shrink-0 rounded-md bg-[#F3F4F6] px-1.5 py-0.5 text-[11px] font-medium tabular-nums text-[#6B7280]">
              {`${shown.length} / ${parsed.length}`}
            </span>
          )}
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              title={t("caseDashboard.logs.clearSearch")}
              aria-label={t("caseDashboard.logs.clearSearch")}
              className="shrink-0 text-[#9CA3AF] hover:text-[#374151]"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <SecondaryButton onClick={() => setFollow((f) => !f)}>
          {follow ? <Pause size={14} /> : <Play size={14} />}
          {follow
            ? t("caseDashboard.logs.pause")
            : t("caseDashboard.logs.follow")}
        </SecondaryButton>
        <SecondaryButton onClick={() => copy(shown)} disabled={!shown.length}>
          <Copy size={14} />
          {t("caseDashboard.logs.copyAll")}
        </SecondaryButton>
        <SecondaryButton
          onClick={() => {
            stream.clear();
            clearSelection();
          }}
        >
          <Trash2 size={14} />
          {t("caseDashboard.logs.clear")}
        </SecondaryButton>
      </div>

      <div className="flex min-h-[32px] flex-wrap items-center gap-3 text-[12px] text-[#6B7280]">
        {selectedLines.length > 0 ? (
          <>
            <span className="font-medium text-[#111827]">
              {t("caseDashboard.logs.selectedCount", {
                count: selectedLines.length,
              })}
            </span>
            <button
              type="button"
              onClick={() => copy(selectedLines)}
              className="flex items-center gap-1 font-medium text-[#C2611F] hover:underline"
            >
              <Copy size={13} />
              {t("caseDashboard.logs.copySelected")}
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="flex items-center gap-1 font-medium hover:text-[#374151]"
            >
              <X size={13} />
              {t("caseDashboard.logs.clearSelection")}
            </button>
          </>
        ) : (
          <span>{t("caseDashboard.logs.selectHint")}</span>
        )}
        {copied != null && (
          <span
            role="status"
            className="ml-auto flex items-center gap-1 font-medium text-[#059669]"
          >
            <Check size={13} />
            {t("caseDashboard.logs.copied", { count: copied })}
          </span>
        )}
      </div>

      {!stream.running && stream.result && (
        <Notice tone="warn">
          <span className="flex items-center gap-3">
            {t("caseDashboard.logs.stopped")}
            <button
              type="button"
              onClick={() => stream.start("logs --tail 300")}
              className="flex items-center gap-1 font-medium underline"
            >
              <RotateCcw size={13} />
              {t("caseDashboard.logs.reconnect")}
            </button>
          </span>
        </Notice>
      )}

      <Card
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
        bodyClassName="flex min-h-0 flex-1"
      >
        <div
          ref={boxRef}
          className="min-h-[400px] flex-1 overflow-auto bg-[#111827] p-3 font-mono text-[11px] leading-relaxed"
        >
          {shown.length === 0 ? (
            <p className="text-[#6B7280]">
              {filtered && parsed.length
                ? t("caseDashboard.logs.noMatches")
                : t("caseDashboard.logs.empty")}
            </p>
          ) : (
            shown.map((l) => {
              const isSelected = selected.has(l.n);
              return (
                <div
                  key={l.n}
                  data-testid="log-line"
                  aria-selected={isSelected}
                  onMouseDown={(e) => {
                    // Shift-click selects lines, not text.
                    if (e.shiftKey) e.preventDefault();
                  }}
                  onClick={(e) => onLineClick(e, l)}
                  className={`-mx-1 cursor-pointer whitespace-pre-wrap break-all rounded px-1 ${
                    LEVEL_COLOR[l.level]
                  } ${
                    isSelected ? "bg-[#E07A3A]/25" : "hover:bg-white/[0.035]"
                  }`}
                >
                  {l.time && (
                    <span className="mr-2 select-none text-[#6B7280]">
                      {l.time.toLocaleTimeString(undefined, { hour12: false })}
                    </span>
                  )}
                  {LEVEL_LABEL[l.level] && (
                    <span className="mr-2 font-semibold">
                      {LEVEL_LABEL[l.level]}
                    </span>
                  )}
                  {l.tag && (
                    <span className="mr-2 text-[#93C5FD]">
                      {highlight(l.tag, search.trim())}
                    </span>
                  )}
                  {highlight(l.message, search.trim())}
                </div>
              );
            })
          )}
        </div>
      </Card>
    </div>
  );
};

export default LogsView;
